import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import {
  IconArrowRight,
  IconFilter,
  IconReplace,
  IconStar,
  IconStarFilled,
  IconTrash,
  IconUserMinus,
} from '@tabler/icons-react'
import { isEmpty, startCase, toString } from 'lodash'
import pluralize from 'pluralize'

import { Button } from '@/components/buttons/Button'
import { setPlaylists, setPlaylistsChanges } from '@/reducers/playlists/playlists.redux'
import {
  deletePlaylist,
  fetchPlaylists,
  patchPlaylist,
  unsubscribeFromPlaylist,
} from '@/reducers/playlists/playlists.actions'
import { PageHeader } from '@/components/headers/PageHeader'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { ButtonAndFormModal } from '@/components/layout/modals/ButtonAndFormModal'
import { ModalActions } from '@/components/modals/ModalActions'
import { OrganizationFilter } from '@/components/filters/OrganizationFilter'
import { FilterForm } from '@/components/filters/FilterForm'
import { formatTime, getOrganizationOptions } from '@/utils/helpers'
import { SearchableDropdown } from '@/components/dropdowns/SearchableDropdown'
import { Pill } from '@/components/pills/Pill'
import { auditColumns } from '@/utils/constants'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { Card } from '@/components/cards/Card'
import { CreateOrUpdatePlaylistButtonAndModal } from './components/CreateOrUpdatePlaylistButtonAndModal'

const PlaylistsPage = () => {
  const dispatch = useDispatch()
  const { playlists, playlistsChanges, loading } = useSelector((state) => state.playlists)
  const organizations = useSelector((state) => state.organizations)
  const currentOrganization = useSelector((state) => state.currentOrganization)
  const users = useSelector((state) => state.organizationUsers.users)
  const [type, setType] = useState('all')
  const { organizationid: currentUserOrgId, user_id: currentUserId } = useSelector(
    (state) => state.currentUser
  )
  const [searchedOrgId, setSearchedOrgId] = useState(currentUserOrgId)
  const isBaltoAdmin = currentUserOrgId === 1

  const handleDeletePlaylist = (playlistUuid) => {
    dispatch(deletePlaylist(playlistUuid, searchedOrgId))
  }

  const handleUnsubscribeFromPlaylist = async (playlist) => {
    await dispatch(unsubscribeFromPlaylist(playlist, currentUserId))
    dispatch(fetchPlaylists(playlist.organization_id))
  }

  const handleOrgSelect = (option, action) => {
    if (action.action === 'clear') {
      setSearchedOrgId(null)
      dispatch(setPlaylists([]))
      dispatch(setPlaylistsChanges([]))
    } else {
      setSearchedOrgId(option.value)
      dispatch(fetchPlaylists(option.value))
    }
  }

  const handleToggleFavorite = async (row) => {
    await dispatch(patchPlaylist(row.uuid, row.organization_id, { is_favorite: !row.is_favorite }))
    await dispatch(fetchPlaylists(searchedOrgId))
  }

  const columns = [
    {
      accessor: 'is_favorite',
      label: '',
      isSortable: false,
    },
    {
      accessor: 'name',
      label: 'Name',
      isSearchable: true,
      format: (value, row) => (
        <Link
          to={`/playlists/${row.playlist_uuid}?organization_id=${searchedOrgId}`}
          className="table-link"
        >
          {value}
        </Link>
      ),
    },
    {
      accessor: 'author',
      label: 'Author',
      isSearchable: true,
    },
    {
      accessor: 'visibility',
      label: 'Visibility',
    },
    {
      accessor: 'call_count',
      label: 'Count',
      isSearchable: true,
      format: (value) => toString(value),
      headerAlignment: 'right',
    },
    {
      accessor: 'created_at',
      label: 'Date Created',
      format: (value) => formatTime(value),
    },
    {
      accessor: 'updated_at',
      label: 'Last Modified',
      format: (value) => formatTime(value),
    },
  ]

  const actions = [
    {
      label: 'Delete',
      trigger: (row) => {
        return (
          <ButtonAndFormModal
            popup
            icon={<IconTrash />}
            buttonProps={{ basic: true, small: true, square: true }}
            modalId={`playlists/delete/${row.playlist_uuid}`}
            modalTitle={`Delete "${row.name}"`}
            modalProps={{ size: 'tiny' }}
            modalContent={
              <p>Are you sure you want to delete this playlist? This action cannot be undone.</p>
            }
            form={
              <ModalActions onSubmit={() => handleDeletePlaylist(row.playlist_uuid)} isDelete />
            }
          />
        )
      },
    },
    {
      label: 'Unsubscribe',
      trigger: (row) => {
        const isUserSubscribedToPlaylist =
          row.allowed_users && row.allowed_users.includes(currentUserId)

        return (
          <ButtonAndFormModal
            popup
            icon={<IconUserMinus />}
            disabled={!isUserSubscribedToPlaylist}
            buttonProps={{
              basic: true,
              small: true,
              square: true,
              disabledTooltip: 'Only invited viewers can leave',
            }}
            modalId={`playlist/edit/${row.playlist_uuid}`}
            modalTitle="Leave Private Playlist"
            modalProps={{ size: 'tiny' }}
            form={
              <ModalActions
                onSubmit={() => handleUnsubscribeFromPlaylist(row)}
                buttonLabel="Leave"
                isDelete
              >
                <p>
                  Are you sure you want to leave this private playlist? You&#39;ll lose access and
                  will need to be re-invited to view this playlist again.
                </p>
              </ModalActions>
            }
          />
        )
      },
    },
  ]
  const getWhatChangedText = (change) => {
    const { visibility, allowed_users, event_type: eventType, name } = change

    const getUserFullName = (id) => {
      const user = users.find((u) => u.id === id)
      return user ? `${user.first_name} ${user.last_name}` : `User ${id}`
    }

    const formatNamesWithPill = (ids) => {
      if (!ids?.length) return null
      const maxVisible = 2
      const visible = ids.slice(0, maxVisible).map(getUserFullName)
      const hidden = ids.slice(maxVisible).map(getUserFullName)
      const hiddenCount = ids.length - maxVisible

      return (
        <>
          {visible.join(', ')}
          {hiddenCount > 0 && (
            <InteractiveTooltip
              content={hidden.map((name) => (
                <div key={name}>{name}</div>
              ))}
            >
              <Pill small data-testid="viewers-pill" className="ml">
                +{hiddenCount} more
              </Pill>
            </InteractiveTooltip>
          )}
        </>
      )
    }

    const getStatus = (visibility, viewers) => {
      if (visibility === true) return 'Public'
      if (visibility === false) return viewers?.length ? 'Shared' : 'Private'
      return ''
    }

    const renderViewers = (added = [], removed = []) => {
      const parts = []
      if (added.length) parts.push('Viewers added:')
      if (removed.length) parts.push('Viewers removed:')

      return (
        <>
          <span data-testid="what-changed-text">{parts.join(' | ')}</span>
          {(added.length || removed.length) && (
            <div className="mt-0" data-testid="what-changed-viewers">
              {added.length > 0 && formatNamesWithPill(added)}
              {added.length > 0 && removed.length > 0 && ', '}
              {removed.length > 0 && formatNamesWithPill(removed)}
            </div>
          )}
        </>
      )
    }

    if (eventType === 'visibility_update' && visibility) {
      const oldViewers = allowed_users?.old ?? []
      const newViewers = allowed_users?.new ?? []
      const added = allowed_users?.added ?? []
      const removed = allowed_users?.removed ?? []

      const oldStatus = getStatus(visibility.old, oldViewers)
      const newStatus = getStatus(visibility.new, newViewers)

      if (oldStatus === 'Shared' && newStatus === 'Shared') {
        return renderViewers(added, removed)
      }

      const addedText = added.length ? formatNamesWithPill(added) : null
      const removedText = removed.length ? formatNamesWithPill(removed) : null

      return (
        <>
          <span data-testid="what-changed-text">
            {oldStatus} <IconArrowRight className="icon-svg" data-testid="visibility-update-icon" />{' '}
            {newStatus}
            {addedText && ' | Viewers added:'}
            {!addedText && removedText && ' | Viewers removed:'}
            {addedText && removedText && ' | Viewers added/removed:'}
          </span>
          {(addedText || removedText) && (
            <div className="mt-0" data-testid="what-changed-viewers">
              {addedText}
              {addedText && removedText && ', '}
              {removedText}
            </div>
          )}
        </>
      )
    }

    if (eventType === 'create_playlist') {
      const status = getStatus(visibility, allowed_users)
      const viewerText = allowed_users?.length ? formatNamesWithPill(allowed_users) : null
      return (
        <>
          <span>{`${status} playlist created`}</span>
          {viewerText && <div className="mt-0">{viewerText}</div>}
        </>
      )
    }

    if (eventType === 'delete_playlist') {
      const status = getStatus(visibility, allowed_users)
      return <span>{`${status} playlist deleted`}</span>
    }
    if (eventType === 'visibility_update' && !visibility && allowed_users) {
      const added = allowed_users.added ?? []
      const removed = allowed_users.removed ?? []

      if (added.length || removed.length) {
        return renderViewers(added, removed)
      }
    }
    if (eventType === 'update_playlist') {
      if (name && typeof name === 'object' && name.old && name.new) {
        return (
          <span data-testid="what-changed-text">
            Playlist name changed:
            <br />
            {name.old} <IconArrowRight className="icon-svg" data-testid="name-update-icon" />{' '}
            {name.new}
          </span>
        )
      }
      return null
    }

    return ''
  }
  const playlistsChangesRows = playlistsChanges?.map((change) => ({
    ...change,
    created_at: formatTime(change.created_at),
    playlists: typeof change.name === 'object' ? change.name?.new ?? '' : change.name,
    what_changed: getWhatChangedText(change),
    event_type: {
      value: change.event_type,
      as: (
        <Pill
          small
          critical={change.event_type === 'delete_playlist'}
          success={change.event_type === 'create_playlist'}
          informative={change.event_type === 'visibility_update'}
          warning={change.event_type === 'update_playlist'}
        >
          {startCase(change.event_type.replace(/_/g, ' '))}
        </Pill>
      ),
    },
  }))

  const formatPlaylistListOfAllowedUsersInfo = (playlist) => {
    const usersToBeShown = playlist.allowed_users?.filter((id) => id !== currentUserId)

    if (isEmpty(usersToBeShown)) {
      return 'This Playlist is shared only with you'
    }

    const userNames = usersToBeShown
      .map((id) => {
        const user = users.find((u) => u.id === id)
        if (user) return `${user.first_name} ${user.last_name}`
        return undefined
      })
      .filter(Boolean)
    const maxNumberOfUsersInPopup = 10
    return (
      <div>
        This Playlist is also visible to:
        {userNames.slice(0, maxNumberOfUsersInPopup).map((fullName) => (
          <div>{fullName}</div>
        ))}
        {userNames.length > maxNumberOfUsersInPopup && (
          <div>{userNames.length - maxNumberOfUsersInPopup} more users can see...</div>
        )}
      </div>
    )
  }

  const getPlaylistVisibilityPopupInfo = (playlist) => {
    const isBaltoAdminAndOrg1Playlist = isBaltoAdmin && playlist.organization_id === 1
    if (isBaltoAdminAndOrg1Playlist) {
      return 'The Playlists from Balto Org are visible to All Balto Admin users'
    }
    const isBaltoAdminAndNonOrg1Playlist = isBaltoAdmin && playlist.organization_id !== 1

    const isPublic = playlist.public_visibility
    if (isPublic) {
      if (isBaltoAdminAndNonOrg1Playlist) {
        const orgName = organizations.find((o) => o.id === playlist.organization_id)?.name
        return `This Playlist is visible to everyone in ${orgName} organization`
      }
      return 'This Playlist is visible to everyone in the organization'
    }

    const isVisibleOnlyToTheAuthor = isEmpty(playlist.allowed_users)
    if (isVisibleOnlyToTheAuthor) {
      return isBaltoAdminAndNonOrg1Playlist
        ? 'This Playlist is visible only to the author'
        : 'This Playlist is visible only to you'
    }

    if (isBaltoAdminAndNonOrg1Playlist) {
      // should we render other org userNames? if yes then we'll need to create a separate API call for each org and possible cache each call result so we avoid unnecessary re-call for the same org
      const numOfAllowedUsers = playlist.allowed_users.length
      const orgName = organizations.find((o) => o.id === playlist.organization_id)?.name
      return `This Playlist is shared with [${numOfAllowedUsers}] ${pluralize('user', numOfAllowedUsers)} from ${orgName} organization`
    }

    return formatPlaylistListOfAllowedUsersInfo(playlist)
  }

  const rows = playlists
    .filter((playlist) => {
      if (type === 'favorites') {
        return playlist.is_favorite
      }

      if (type === 'my') {
        return playlist.user_id === currentUserId
      }

      return true
    })
    .map((playlist) => ({
      ...playlist,
      playlist_uuid: playlist.uuid,
      is_favorite: {
        cellProps: {
          className: 'collapsing compact-cell',
        },
        value: playlist.is_favorite,
        as: (
          <InteractiveTooltip
            placement="top-start"
            content={playlist.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Button small square basic onClick={() => handleToggleFavorite(playlist)}>
              {playlist.is_favorite ? <IconStarFilled className="fill-warning" /> : <IconStar />}
            </Button>
          </InteractiveTooltip>
        ),
      },
      visibility: {
        cellProps: {
          className: 'collapsing compact-cell',
        },
        value: playlist.public_visibility ? 'Public' : 'Private',
        as: (
          <InteractiveTooltip content={getPlaylistVisibilityPopupInfo(playlist)}>
            <Pill small>{startCase(playlist.public_visibility ? 'Public' : 'Private')}</Pill>
          </InteractiveTooltip>
        ),
      },
    }))

  useEffect(() => {
    dispatch(fetchPlaylists(currentUserOrgId))
  }, [])

  return (
    <>
      <PageHeader
        data-testid="playlists-page"
        title="Playlists"
        actions={
          <>
            {!isEmpty(playlists) && !isEmpty(playlistsChangesRows) && (
              <ButtonAndFormModal
                buttonLabel="Change History"
                icon={<IconReplace />}
                modalId="playlists/audit"
                modalProps={{ size: 'large' }}
                modalTitle="Change History"
                modalContent={
                  <div className="modal-content-wrapper">
                    <Card className="no-padding table-segment">
                      <AdvancedTable
                        loading={loading.playlists}
                        rows={playlistsChangesRows}
                        columns={auditColumns}
                        pagination
                        defaultOrder="desc"
                        sortEnabled={false}
                      />
                    </Card>
                  </div>
                }
              />
            )}
            <CreateOrUpdatePlaylistButtonAndModal
              action="create"
              organization={
                isBaltoAdmin
                  ? organizations.find((org) => org.id === searchedOrgId)
                  : currentOrganization
              }
            />
          </>
        }
      />

      <>
        <FilterForm
          small
          sticky={false}
          inlineActions="end"
          actions={
            <SearchableDropdown
              hideSearch
              options={[
                { value: 'favorites', label: 'Favorites' },
                { value: 'my', label: 'Created by me' },
                { value: 'all', label: 'Created by anyone' },
              ]}
              selected={type}
              handleSelect={(value) => setType(value)}
              icon={<IconFilter />}
            />
          }
        >
          {isBaltoAdmin && (
            <OrganizationFilter
              options={getOrganizationOptions(organizations)}
              value={searchedOrgId}
              onChange={handleOrgSelect}
            />
          )}
        </FilterForm>
        <Card className="no-padding">
          <AdvancedTable
            index="playlist_uuid" // note: "uuid" does not access the accessor
            loading={loading.playlists}
            rows={rows}
            columns={columns}
            actions={actions}
            pagination
            stickyAction
            defaultOrderBy="created_at"
            defaultOrder="desc"
            pinned="is_favorite"
          />
        </Card>
      </>
    </>
  )
}

export default PlaylistsPage
